import { Button } from 'components/common/Button'
import { Icon } from 'components/common/Icon'
import { Tag } from 'components/common/Tag'
import { ExtendedNavContainer } from 'components/nav/ExtendedNavContainer'
import { ExtendedNavSection } from 'components/nav/ExtendedNavSection'
import { ExtendedNavSectionSkeleton } from 'components/nav/ExtendedNavSectionSkeleton'
import routes from 'constants/routes'
import { useArtistsNavigationItems } from 'hooks/nav/useArtistsNavigationItems'
import useTranslation from 'next-translate/useTranslation'
import { PropsWithChildren } from 'react'
import { titleCase } from 'utils/strings'

export function ArtistsExtendedNav({ children }: PropsWithChildren) {
  const { t } = useTranslation('common')
  const {
    blueChipArtists,
    mostPopularArtists,
    trendingArtists,
    nationalities,
  } = useArtistsNavigationItems()

  return (
    <ExtendedNavContainer trigger={children}>
      <div className='grid grid-cols-4 gap-x-4'>
        {!blueChipArtists && <ExtendedNavSectionSkeleton />}
        {blueChipArtists && (
          <ExtendedNavSection
            label={t('extended_nav.blue_chip_artists')}
            items={blueChipArtists.map(artist => ({
              href: artist.href,
              label: (
                <>
                  <span>{artist.name}</span>
                  <Tag size='XS' color='light-purple' icon={null}>
                    {t('extended_nav.x_works', { x: artist.totalArtworks })}
                  </Tag>
                </>
              ),
            }))}
          />
        )}

        {!mostPopularArtists && <ExtendedNavSectionSkeleton />}
        {mostPopularArtists && (
          <ExtendedNavSection
            label={t('extended_nav.most_popular_artists')}
            items={mostPopularArtists.map(artist => ({
              href: artist.href,
              label: (
                <>
                  <span>{artist.name}</span>
                  <Tag size='XS' color='light-purple' icon={null}>
                    {t('extended_nav.x_works', { x: artist.totalArtworks })}
                  </Tag>
                </>
              ),
            }))}
          />
        )}

        {!trendingArtists && <ExtendedNavSectionSkeleton />}
        {trendingArtists && (
          <ExtendedNavSection
            label={t('extended_nav.trending_artists')}
            items={trendingArtists.map(artist => ({
              href: artist.href,
              label: (
                <>
                  <span>{artist.name}</span>
                  <Tag
                    size='XS'
                    color='success-light'
                    icon='arrow-up'
                    iconSize={10}
                  >
                    {artist.trendingPercent}%
                  </Tag>
                </>
              ),
            }))}
          />
        )}

        <ExtendedNavSection
          label={t('extended_nav.nationality')}
          items={nationalities}
        />
      </div>

      <Button
        style='solid-primary'
        pill
        type='link'
        href={routes.artists}
        className='mt-4 w-max space-x-2'
        size='sm'
      >
        <span>{titleCase(t('view_all_artists'))}</span>
        <Icon name='arrow-going-up' size={16} />
      </Button>
    </ExtendedNavContainer>
  )
}
